import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "../common/ReportTable";
import TableSkeleton from "../../utils/TableSkeleton";
import EmptyTableState from "../../utils/EmptyTableState";
import * as Constants from "../../utils/Constants";
import {customStyles} from "../../utils/DataTableCustomStyle";
import {CardList} from "../CardView";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExternalLinkAlt, faFileExcel, faFilePdf} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import "../../styles/components/reports.css";
import "../../styles/components/alert-log.css";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const parseTs = (v) => {
  if (!v) return null;
  if (typeof v === "number") return new Date(v * 1000);
  const normalized = String(v).replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  return new Date(normalized);
};

const ALERT_TYPES = [
  "ENGINE_ON",
  "ENGINE_OFF",
  "OVERSPEED",
  "LOW_BATTERY",
  "BATTERY_CRITICAL",
  "TAMPER_DETECTED",
  "TAMPER_CLEARED",
  "DEVICE_ONLINE",
  "DEVICE_OFFLINE",
  "GPS_FIX_LOST",
  "GPS_FIX_ACQUIRED",
];

// REALTIME = alert types that trigger an immediate WhatsApp (overspeed, tamper,
// battery-critical); WARNING = batched into the daily digest; INFO = audit-only.
const SEVERITIES = ["REALTIME", "CRITICAL", "WARNING", "INFO"];

const SEVERITY_BADGE = {
  REALTIME: {bg: "#ffedd5", color: "#9a3412"},
  CRITICAL: {bg: "#fee2e2", color: "#991b1b"},
  WARNING: {bg: "#fef3c7", color: "#92400e"},
  INFO: {bg: "#dbeafe", color: "#1e40af"},
};

const Gt06AlertLog = () => {
  const [assetList, setAssetList] = useState([]);
  const [checkedAssets, setCheckedAssets] = useState({});
  const [checkedTypes, setCheckedTypes] = useState({});
  const [checkedSeverities, setCheckedSeverities] = useState({});
  const TODAY = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);

  // Load GT06 asset list once
  useEffect(() => {
    (async () => {
      try {
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`
        );
        const all = res.data?.responseData || [];
        setAssetList(all.filter((a) => (a.assetType || "").toUpperCase() === "GT06_TRACKER"));
      } catch (err) {
        console.error("Error fetching GT06 assets:", err);
      }
    })();
  }, []);

  const fetchPage = async (p = 0, size = pageSize) => {
    if (!startDate || !endDate) {
      showToast("Please select a date range.", {type: "error"});
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        timezone: TZ,
        page: String(p),
        size: String(size),
      });
      const assetIds = Object.keys(checkedAssets).filter((k) => checkedAssets[k]);
      const types = Object.keys(checkedTypes).filter((k) => checkedTypes[k]);
      const sevs = Object.keys(checkedSeverities).filter((k) => checkedSeverities[k]);
      if (assetIds.length) params.set("assetIds", assetIds.join(","));
      if (types.length) params.set("alertTypes", types.join(","));
      if (sevs.length) params.set("severities", sevs.join(","));

      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/gt06-reports/alert-log?${params}`
      );
      const data = res.data || {};
      const enriched = (data.content || []).map((r) => ({
        ...r,
        assetName: assetList.find((a) => a.assetUnitId === r.assetUnitId)?.name || r.assetUnitId,
      }));
      setRows(enriched);
      setTotalRows(data.totalElements || 0);
      setPage(p);
    } catch (err) {
      console.error("Alert log failed:", err);
      showToast(err?.response?.data?.responseMessage || "Failed to load alert log", {type: "error"});
    } finally {
      setIsLoading(false);
    }
  };

  const onSearch = () => fetchPage(0, pageSize);

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setCheckedAssets({});
    setCheckedTypes({});
    setCheckedSeverities({});
    setRows([]);
    setTotalRows(0);
    setPage(0);
  };

  const toggleAsset = (id) => setCheckedAssets((p) => ({...p, [id]: !p[id]}));
  const toggleType = (t) =>
    setCheckedTypes((p) => ({...p, [t]: !p[t]}));
  const toggleSev = (s) => setCheckedSeverities((p) => ({...p, [s]: !p[s]}));

  // Type counts shown above the table — quick situational awareness.
  const typeCounts = rows.reduce((m, r) => {
    m[r.alertType] = (m[r.alertType] || 0) + 1;
    return m;
  }, {});

  const fmtDt = (v) => { const d = parseTs(v); return d && !isNaN(d) ? d.toLocaleString() : "—"; };

  const columns = [
    {
      name: "Time", id: "Time",
      selector: (r) => r.triggeredAt,
      sortable: true,
      grow: 1.8,
      minWidth: "155px",
      cell: (r) => fmtDt(r.triggeredAt),
    },
    {name: "Asset", id: "Asset", selector: (r) => r.assetName, sortable: true, grow: 2, minWidth: "130px"},
    {
      name: "Alert Type", id: "Alert Type",
      selector: (r) => r.alertType,
      sortable: true,
      grow: 1.5,
      minWidth: "140px",
      cell: (r) => <span className="alert-type-cell">{r.alertType}</span>,
    },
    {
      name: "Severity", id: "Severity",
      selector: (r) => r.severity,
      grow: 1,
      minWidth: "90px",
      cell: (r) => {
        const c = SEVERITY_BADGE[r.severity] || SEVERITY_BADGE.INFO;
        return (
          <span className="severity-badge" style={{background: c.bg, color: c.color}}>
            {r.severity}
          </span>
        );
      },
    },
  ];

  const ExpandedRow = ({data: r}) => {
    const fields = [
      ["IMEI", r.assetUnitId],
      ["State", r.currentState ? "ACTIVE" : "CLEARED"],
      ["Speed", r.speedKmph != null ? `${r.speedKmph} km/h` : "—"],
      ["ACC", r.accOn == null ? "—" : r.accOn ? "ON" : "OFF"],
    ];
    return (
      <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", padding: "10px 20px 12px", background: "#fafbff", borderBottom: "2px solid #e9ecef", borderLeft: "4px solid #FF683A"}}>
        {fields.map(([label, value], i) => (
          <div key={label} style={{display: "flex", flexDirection: "column", gap: "3px", padding: "2px 20px 2px 0", marginRight: "20px", borderRight: "1px solid #e5e7eb"}}>
            <span style={{fontSize: "0.62rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em"}}>{label}</span>
            <span style={{fontSize: "0.84rem", fontWeight: 500, color: "#1f2937"}}>{value}</span>
          </div>
        ))}
        {r.latitude != null && r.longitude != null && (
          <a
            href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="alert-log-map-link"
            style={{marginLeft: "4px", fontSize: "0.84rem"}}
          >
            View on Map <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        )}
      </div>
    );
  };

  const exportExcel = () => {
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        Time: r.triggeredAt,
        Asset: r.assetName,
        IMEI: r.assetUnitId,
        AlertType: r.alertType,
        Severity: r.severity,
        State: r.currentState ? "ACTIVE" : "CLEARED",
        SpeedKmph: r.speedKmph,
        AccOn: r.accOn,
        Latitude: r.latitude,
        Longitude: r.longitude,
      }))
    );
    const wb = {Sheets: {data: ws}, SheetNames: ["AlertLog"]};
    const buf = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    FileSaver.saveAs(new Blob([buf]), `gt06_alert_log_${Date.now()}.xlsx`);
  };

  const exportPdf = () => {
    if (!rows.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(`GT06 Alert Log — ${startDate} to ${endDate}`, 30, 30);
    autoTable(doc, {
      startY: 50,
      head: [["Time", "Asset", "IMEI", "Type", "Severity", "State", "Speed", "ACC"]],
      body: rows.map((r) => [
        fmtDt(r.triggeredAt),
        r.assetName,
        r.assetUnitId,
        r.alertType,
        r.severity,
        r.currentState ? "ACTIVE" : "CLEARED",
        r.speedKmph != null ? `${r.speedKmph} km/h` : "—",
        r.accOn == null ? "—" : r.accOn ? "ON" : "OFF",
      ]),
      styles: {fontSize: 8},
    });
    doc.save(`gt06_alert_log_${Date.now()}.pdf`);
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            Alert Log {totalRows > 0 && `(${totalRows})`}
          </span>
          {rows.length > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportPdf}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        {Object.keys(typeCounts).length > 0 && (
          <div className="alert-log-type-chips">
            {Object.entries(typeCounts).map(([t, n]) => (
              <span key={t} className="alert-log-chip">
                <strong>{n}</strong> {t}
              </span>
            ))}
          </div>
        )}

        <div className="report-table-wrapper">
          <ReportTable
            columns={columns}
            data={rows}
            customStyles={customStyles}
            progressPending={isLoading}
            progressComponent={<TableSkeleton />}
            fixedHeader
            fixedHeaderScrollHeight="100%"
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={page + 1}
            paginationPerPage={pageSize}
            onChangeRowsPerPage={(s) => {
              setPageSize(s);
              fetchPage(0, s);
            }}
            onChangePage={(p) => fetchPage(p - 1, pageSize)}
            noDataComponent={<EmptyTableState variant="search" />}
            expandableRows
            expandableRowExpanded={() => true}
            expandableRowsComponent={ExpandedRow}
          />
        </div>
      </div>

      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>
        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">GPS Trackers (leave empty for all)</label>
            <div className="asset-list-wrapper">
              <CardList data={assetList} checkedItems={checkedAssets} toggleCheckbox={toggleAsset} />
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">Alert Types (leave empty for all)</label>
            <div className="alert-log-filter-chips">
              {ALERT_TYPES.map((t) => (
                <label key={t} className={`alf-chip ${checkedTypes[t] ? "active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!!checkedTypes[t]}
                    onChange={() => toggleType(t)}
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">Severity</label>
            <div className="alert-log-filter-chips">
              {SEVERITIES.map((s) => (
                <label key={s} className={`alf-chip ${checkedSeverities[s] ? "active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!!checkedSeverities[s]}
                    onChange={() => toggleSev(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={startDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              value={endDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearch}>Search</button>
            <button className="btn-report btn-secondary" onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gt06AlertLog;
